import * as React from 'react';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {IMediaItem, IVideoPlayer} from '../../../types/app-types';
import classNames from 'classnames';
import s from './ProductGalleryProvider.scss';
import {getMainImageRatio, isVideo} from '@wix/wixstores-client-core/dist/es/src/media/mediaService';
import _ from 'lodash';
import {useContext} from 'react';
import {SwiperClass} from 'swiper/react';
import {SPECS} from '../../../specs';

export interface ProductGalleryProviderProps extends ProvidedGlobalProps {
  imageRatioId: number;
  media: IMediaItem[];
  withImageRatio: boolean;
  paddingTopOverride?: string;
}

interface ProductGalleryProviderState {
  selectedIndex: number;
  mainMediaSwiper: SwiperClass;
  videoPlayers: Record<number, IVideoPlayer>;
  hasSelectedIndexChanged: boolean;
}

export interface IProductGalleryContext {
  selectedIndex: number;
  registerVideoPlayer(player: IVideoPlayer, index: number): void;
  changeSelectedIndex(selectedIndex: number): void;
  hasSelectedIndexChanged: boolean;
  imageRatioId: number;
  setMainMediaSwiper(swiper: SwiperClass): void;
  mainMediaSwiper: SwiperClass;
}

export const ProductGalleryContext = React.createContext({
  selectedIndex: 0,
  registerVideoPlayer: _.noop,
  changeSelectedIndex: _.noop,
  hasSelectedIndexChanged: false,
  imageRatioId: -1,
  setMainMediaSwiper: _.noop,
  mainMediaSwiper: {} as SwiperClass,
} as IProductGalleryContext);

export const useProductGallery = () => useContext(ProductGalleryContext);

class ProductGalleryProviderComponent extends React.Component<
  ProductGalleryProviderProps,
  ProductGalleryProviderState
> {
  public state = {
    selectedIndex: 0,
    hasSelectedIndexChanged: false,
    mainMediaSwiper: null,
    videoPlayers: {},
  };

  private readonly registerVideoPlayer = (player: IVideoPlayer, index: number) => {
    const {videoPlayers} = this.state;
    videoPlayers[index] = player;
    this.setState({videoPlayers});
  };

  private readonly changeSelectedIndex = (newlySelectedIndex: number) => {
    const {videoPlayers, mainMediaSwiper, selectedIndex} = this.state;
    if (videoPlayers[selectedIndex]) {
      videoPlayers[selectedIndex].pause();
    }
    this.setState({selectedIndex: newlySelectedIndex});
    this.setState({hasSelectedIndexChanged: true});
    mainMediaSwiper?.slideTo(newlySelectedIndex);
    if (videoPlayers[newlySelectedIndex]) {
      videoPlayers[newlySelectedIndex].play();
    }
  };

  private readonly setMainMediaSwiper = (mainMediaRef: SwiperClass) => {
    const {mainMediaSwiper} = this.state;
    if (mainMediaRef && mainMediaSwiper !== mainMediaRef) {
      this.setState({mainMediaSwiper: mainMediaRef});
    }
  };

  private readonly getImageRationClass = (media: IMediaItem, imageRatioId: number) => {
    const ratioClassSuffix = getMainImageRatio(media, imageRatioId).title.replace(':', '-');
    return `image-ratio-${ratioClassSuffix}`;
  };

  private readonly getComposite = () => {
    const {
      children,
      globals: {isMobile, useExperiments},
      imageRatioId,
      paddingTopOverride,
      media,
      withImageRatio,
    } = this.props;
    const isNavigationPerBreakpointEnabled = useExperiments.enabled(SPECS.PPNavigationSectionPerBreakpoint);
    if (isNavigationPerBreakpointEnabled) {
      return (
        <div data-hook="product-gallery-composite" className={s.root}>
          {children}
        </div>
      );
    }

    const keyMedia = media ? media[0] : null;
    const imageRatio = this.getImageRationClass(keyMedia, imageRatioId);

    const getDynamicPaddingTop = () => {
      if (!withImageRatio || isMobile || !keyMedia || isVideo(keyMedia)) {
        return undefined;
      }

      const {width, height} = keyMedia;
      const ratio = (height / width) * 100;
      return `${ratio}%`;
    };

    const dynamicPaddingTop = getDynamicPaddingTop();
    const customPaddingTop = dynamicPaddingTop && imageRatioId === -1 && !isMobile;

    const productGalleryComposite = classNames(
      {[s.productGallery]: withImageRatio},
      {[s.withImageRatio]: withImageRatio && !isMobile},
      {[s[imageRatio]]: withImageRatio && !isMobile},
      {[s.productGallery]: !withImageRatio}
    );

    const responsivePaddingTop = customPaddingTop ? dynamicPaddingTop : undefined;
    const inlineStyles = {paddingTop: paddingTopOverride ?? responsivePaddingTop};

    return (
      <div data-hook="product-gallery-composite" className={productGalleryComposite} style={inlineStyles}>
        {children}
      </div>
    );
  };

  public render(): JSX.Element {
    const {media, imageRatioId} = this.props;
    const {selectedIndex, hasSelectedIndexChanged, mainMediaSwiper} = this.state;

    return media ? (
      <ProductGalleryContext.Provider
        value={{
          selectedIndex,
          registerVideoPlayer: this.registerVideoPlayer,
          changeSelectedIndex: this.changeSelectedIndex,
          hasSelectedIndexChanged,
          mainMediaSwiper,
          setMainMediaSwiper: this.setMainMediaSwiper,
          imageRatioId,
        }}>
        {this.getComposite()}
      </ProductGalleryContext.Provider>
    ) : (
      this.getComposite()
    );
  }
}

export const ProductGalleryProvider = withGlobalProps(ProductGalleryProviderComponent);
